import { ONLINE_RESUME_OFFER_MS, type GameId } from "@bing/shared";

/**
 * SAME-DEVICE online re-join. An online table is a LIVE room, not a saved game — so "resuming" it
 * means reconnecting to it and reclaiming your seat from the bot (see the game-server's
 * `TableController.reclaim`). Colyseus lets a client back into a locked, in-progress room ONLY with
 * the room's `reconnectionToken`, which lives on the in-memory Room object and dies when the app
 * closes. So we stash it here (localStorage) — keyed to THIS device — alongside the roomId + game,
 * and the «ادامه بازی» strip offers it back. Cross-device re-join is intentionally out of scope.
 *
 * The token rotates on every (re)connect, so it is re-saved each time; it is cleared when the match
 * ends (gameover) or a re-join is refused (the room is gone / finished / the seat isn't reclaimable).
 */
export interface SavedOnlineGame {
  roomId: string;
  game: GameId;
  /** The live Colyseus `room.reconnectionToken` for this seat (rotates each connect). */
  token: string;
  /**
   * RESUMABLE ONLINE GAMES (chess) — the stable `OnlineGame` row this table persists to,
   * as announced by the room. The SAME game is also listed from the DB on «ادامه بازی», so
   * without this the strip showed it TWICE (once from here, once from the server) with no
   * way to tell the two rows apart. Absent for every card table.
   */
  onlineGameId?: string;
  /**
   * THE TABLE HAD NOT DEALT WHEN THIS WAS SAVED — it is a waiting room (or a حکم/شلم «یار یا
   * حریف» step), not a game in progress.
   *
   * Only a PRIVATE table is ever saved pre-start, and only because its seat is genuinely held:
   * `onLeave`'s pre-start branch mints a THIRTY-MINUTE reservation for one, and the same
   * `client.reconnect(token)` route that reclaims a dropped seat mid-game walks straight past the
   * room lock that refuses their own invite link. A PUBLIC waiting room gets nothing — its hold is
   * 120 s and a public table whose only human drops is reaped at the drop, so the offer could only
   * ever be a failure.
   *
   * The flag exists so the «ادامه بازی» strip can say what it is: the heading there reads «بازی‌های
   * در جریان», and calling a lobby a game in progress is exactly the phantom the save gate was
   * written to prevent. Absent (the normal case) means a dealt game, so every existing entry reads
   * unchanged. Re-saved WITHOUT it the moment the table deals — one entry covers both states,
   * because the pre-start token still reclaims the seat into the running hand (measured on
   * staging 2026-08-31).
   */
  pending?: boolean;
  /**
   * A PUBLIC table (quick-match), not a private one (card-a5 item 1, 2026-10-11).
   *
   * The operator's rule — «reopening the app while the seat is still held goes straight back to the
   * table» — is about PUBLIC games, where leaving costs a loss; a private table's «خروج» keeps this
   * ticket on purpose (its seat stays reclaimable) and must never drag its owner back on the next
   * open. So the straight-back return (`straight-back.ts`) acts only on a ticket that says it is
   * public. Absent (every ticket an older bundle saved, every private table) means NOT public — the
   * safe direction: no straight back, the strip offers it as before.
   */
  public?: boolean;
  savedAt: number;
}

const KEY = "bing:online-game";

/**
 * How long a stashed re-join token is worth offering.
 *
 * The token only opens a room that is still RUNNING, and an online table does not survive
 * anywhere near this long (a game ends, a room disposes, a deploy recreates the server). A
 * day-old entry was still offered on «ادامه بازی» and could only ever fail — and for chess it
 * also suppressed the DB-backed entry that WOULD have worked (see the lobby's dedupe). Twelve
 * hours is far longer than any real table and far shorter than the row's own week.
 *
 * ⚠️ SHARED WITH THE GAME-SERVER'S `TABLE_STATE_TTL_SEC`, and that is the point: for as long as we
 * OFFER a dead table, its fate has to still be on record, or `tableJoinReason` falls back to the
 * vague «این میز دیگر در دسترس نیست» instead of saying what actually happened. The two used to be
 * 12h here and 6h there — see {@link ONLINE_RESUME_OFFER_MS}.
 */
const MAX_AGE_MS = ONLINE_RESUME_OFFER_MS;

export function saveOnlineGame(g: Omit<SavedOnlineGame, "savedAt">): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...g, savedAt: Date.now() }));
  } catch {
    // private mode / quota — re-join just won't be offered; nothing else breaks.
  }
}

export function loadOnlineGame(): SavedOnlineGame | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const g = JSON.parse(raw) as Partial<SavedOnlineGame>;
    if (typeof g.roomId === "string" && typeof g.game === "string" && typeof g.token === "string") {
      // Too old to be a live room any more — drop it rather than offer a certain failure.
      if (typeof g.savedAt === "number" && Date.now() - g.savedAt > MAX_AGE_MS) {
        localStorage.removeItem(KEY);
        return null;
      }
      return {
        roomId: g.roomId,
        game: g.game,
        token: g.token,
        ...(typeof g.onlineGameId === "string" ? { onlineGameId: g.onlineGameId } : {}),
        ...(g.pending === true ? { pending: true } : {}),
        ...(g.public === true ? { public: true } : {}),
        savedAt: g.savedAt ?? 0,
      };
    }
  } catch {
    // fall through
  }
  return null;
}

/**
 * A ROTATED TOKEN MUST REACH THE SAVED TICKET EVEN WHEN NOBODY IS RENDERING THE TABLE.
 *
 * Every (re)connect rotates the seat's token, and the server honours only the newest — the old one
 * is spent the moment the new socket joins. The table screen saves it from a React effect, so a
 * reconnect that lands after the screen is gone used to rotate it into thin air: tapped while that
 * reconnect was in flight, the overlay's «خروج» unmounts the screen, the attempt lands anyway, the
 * server hands the seat to it, the app closes it the way a drop closes (`manageRoom`) — and the
 * server's new reservation is keyed on a token this device never kept. «ادامه بازی» then asked
 * with the spent one and was refused (staging, 2026-10-02,
 * `apps/web/tools/reconnect-probes/probe-resume-after-exit.cjs` SHAPE=hold).
 *
 * Narrow on purpose: it only REFRESHES an entry that already names THIS room. Whether a table
 * belongs on the strip at all stays the save effect's call, and a ticket somebody cleared (the
 * match ended, «خروج بدون بازگشت», a refused re-join) stays cleared.
 */
export function refreshOnlineGameToken(roomId: string, token: string): void {
  const saved = loadOnlineGame();
  if (!saved || saved.roomId !== roomId || saved.token === token) return;
  saveOnlineGame({
    roomId: saved.roomId,
    game: saved.game,
    token,
    ...(saved.onlineGameId !== undefined ? { onlineGameId: saved.onlineGameId } : {}),
    ...(saved.pending === true ? { pending: true } : {}),
    // ⚠️ Rebuilt field by field — a field not carried here is DROPPED by the first reconnect's
    // refresh, and a public ticket that loses `public` is never straight-backed again (item 1).
    ...(saved.public === true ? { public: true } : {}),
  });
}

/** Clear the saved game. With `roomId`, only clears if it matches (don't wipe a NEWER game). */
export function clearOnlineGame(roomId?: string): void {
  try {
    if (roomId) {
      const current = loadOnlineGame();
      if (current && current.roomId !== roomId) return;
    }
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
